import { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { ScenarioAutocomplete } from '@/components/ui/scenario-autocomplete';
import { TariffAutocomplete } from '@/components/ui/tariff-autocomplete';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { PriorityInput, type PriorityItem } from '@/components/ui/priority-input';
import { FieldList } from '@/components/ui/field-list';
import { PeriodInput } from '@/components/ui/period-input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus } from 'lucide-react';
import { RateTypeEnum } from '@/graphql/generated';
import { useRateTypeLookup } from '@/hooks/useRateTypeLookup';
import type { UseFormReturn } from 'react-hook-form';
import type { PricingPolicyFormValues } from './pricing-policy-input';

interface BillingCycleOption {
	billingCycleId: number;
	name: string;
}

const createEmptyStep = () => ({
	rateStepId: undefined,
	intervalMinutes: 1,
	intervalCharge: 0,
	maxIntervals: undefined
});

interface PricingPolicyFormProps {
	mode: 'create' | 'edit' | 'clone';
	form: UseFormReturn<PricingPolicyFormValues>;
	billingCycles: BillingCycleOption[];
	existingPriorities?: PriorityItem[];
	onSubmit: (values: PricingPolicyFormValues) => Promise<void> | void;
	isSubmitting: boolean;
	initialScenarioLabel?: string;
	initialTariffLabel?: string;
	headerTitle: string;
	onBack?: () => void;
}

export const PricingPolicyForm = ({
	mode,
	form,
	billingCycles,
	existingPriorities = [],
	onSubmit,
	isSubmitting,
	initialScenarioLabel,
	initialTariffLabel,
	headerTitle,
	onBack
}: PricingPolicyFormProps) => {
	const { t } = useTranslation('pricingPolicies');
	const { getRateTypeTitle, isTieredRate } = useRateTypeLookup();
	const nameInputRef = useRef<HTMLInputElement>(null);

	const rateTypeEnum = form.watch('rate.rateTypeEnum');
	const overstayRateTypeEnum = form.watch('overstayRate.rateTypeEnum');
	const isTiered = isTieredRate(rateTypeEnum);
	const isOverstayTiered = isTieredRate(overstayRateTypeEnum);

	const handleRateTypeChange = (value: 'TIERED' | 'FLAT_RATE') => {
		form.setValue('rate.rateTypeEnum', value, { shouldValidate: false });
		if (value === 'TIERED') {
			form.setValue('rate.flatRateAmount', undefined, { shouldValidate: false });
			const currentSteps = form.getValues('rate.rateSteps') || [];
			if (currentSteps.length === 0) {
				form.setValue('rate.rateSteps', [createEmptyStep()], { shouldValidate: false });
			}
		} else {
			form.setValue('rate.rateSteps', [], { shouldValidate: false });
		}
	};

	const handleOverstayRateTypeChange = (value: 'NONE' | 'TIERED' | 'FLAT_RATE') => {
		if (value === 'NONE') {
			// Unregister fully removes the field from form state so Zod's .optional() treats it as undefined
			form.unregister('overstayRate');
			// Re-set to undefined to ensure the watched value updates
			form.setValue('overstayRate', undefined as any, { shouldValidate: false });
			// Clear any lingering validation errors on the overstay rate
			form.clearErrors('overstayRate');
			return;
		}
		form.setValue('overstayRate.rateTypeEnum', value, { shouldValidate: false });
		if (value === 'TIERED') {
			form.setValue('overstayRate.flatRateAmount', undefined, { shouldValidate: false });
			const currentSteps = form.getValues('overstayRate.rateSteps') || [];
			if (currentSteps.length === 0) {
				form.setValue('overstayRate.rateSteps', [createEmptyStep()], { shouldValidate: false });
			}
		} else {
			form.setValue('overstayRate.rateSteps', [], { shouldValidate: false });
		}
	};

	// Auto-focus name field when creating a new pricing policy
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="pricing-policy-form"
					/>
				}
			/>
			<Form {...form}>
				<form id="pricing-policy-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.tariffSection', t('form.tariff'))}
							</h2>
						</div>

						<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<FormField
								control={form.control}
								name="tariffId"
								rules={{ required: t('form.tariffRequired') }}
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('form.tariff')}</FormLabel>
										<FormControl>
											<TariffAutocomplete
												value={field.value || ''}
												onChange={tariffId =>
													field.onChange(tariffId)
												}
												placeholder={t(
													'form.tariffPlaceholder'
												)}
												className="w-full"
												initialSelectedLabel={
													initialTariffLabel
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</Card>

					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.generalInfo')}
							</h2>
						</div>

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="name"
							rules={{ required: t('form.nameRequired') }}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.name')}</FormLabel>
									<FormControl>
										<Input {...field} ref={nameInputRef} autoComplete="off" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="priority"
							rules={{
								required: t('form.priorityRequired'),
								validate: value => {
									const num = parseInt(value, 10);
									if (isNaN(num)) return t('form.priorityWholeNumber');
									if (num < 0 || num > 1000) return t('form.priorityRange');
									return true;
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.priority')}</FormLabel>
									<FormControl>
										<PriorityInput
											value={field.value}
											onChange={field.onChange}
											existingItems={existingPriorities}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="scenarioId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.scenario')}</FormLabel>
									<FormControl>
										<ScenarioAutocomplete
											value={field.value || ''}
											onChange={scenarioId =>
												field.onChange(scenarioId)
											}
											placeholder={t(
												'form.scenarioPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialScenarioLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					</Card>

					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.ratesSection')}
							</h2>
						</div>

					<div className="space-y-6">
						{/* Rate Type Selection */}
						<FormField
							control={form.control}
							name="rate.rateTypeEnum"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.rateType', 'Rate Type')} *</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={handleRateTypeChange}
											className="flex flex-row space-x-6"
										>
											<div className="flex items-center space-x-2">
												<RadioGroupItem value="TIERED" id="rate-tiered" />
												<Label htmlFor="rate-tiered" className="text-sm cursor-pointer">
													{getRateTypeTitle(RateTypeEnum.Tiered)}
												</Label>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem value="FLAT_RATE" id="rate-flat" />
												<Label htmlFor="rate-flat" className="text-sm cursor-pointer">
													{getRateTypeTitle(RateTypeEnum.FlatRate)}
												</Label>
											</div>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Flat Rate Amount */}
						{!isTiered && (
							<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								<FormField
									control={form.control}
									name="rate.flatRateAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('form.flatRateAmount', 'Flat Rate Amount')} *</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="number"
													step="0.01"
													min={0}
													value={field.value ?? ''}
													onChange={e => field.onChange(
														e.target.value === '' ? null : Number.parseFloat(e.target.value) || 0
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}

						{/* Tiered Rate Steps */}
						{isTiered && (
							<div className="space-y-4">
								<FieldList
									form={form}
									name="rate.rateSteps"
									className="space-y-4"
									itemsGridClassName="flex flex-col gap-4"
									title={t('form.pricingSteps', 'Pricing Steps')}
									description={t('form.stepEmptyMessage', 'Add a step to define pricing steps.')}
									defaultItem={createEmptyStep()}
									idField="rateStepId"
									forceHardDelete={true}
									addButtonLabel={t('form.addStep', 'Add Step')}
									emptyMessage={t('form.stepEmptyMessage', 'Add a step to define pricing steps.')}
									fields={[
										{
											name: 'intervalMinutes',
											isKeyField: true,
											colSpan: 'col-span-full',
											render: ({ form: f, path, isNewItem }) => (
												<div className="space-y-4">
													{isNewItem && (
														<div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
															<Plus className="w-4 h-4" />
															<span>{t('form.addStep', 'Add Step')}</span>
														</div>
													)}
													<div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.chargeLabel', 'Charge')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.intervalCharge` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0 w-32">
																	<FormControl>
																		<Input
																			{...field}
																			type="number"
																			min={0}
																			step="0.01"
																			onChange={e => {
																				const value = e.target.value === '' ? null : Number.parseFloat(e.target.value);
																				field.onChange(Number.isNaN(value) ? null : value);
																			}}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.perLabel', 'per')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.intervalMinutes` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0">
																	<FormControl>
																		<PeriodInput value={field.value} onChange={field.onChange} />
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.minutesLabel', 'minutes')}
														</span>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.forMaximumLabel', 'for maximum')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.maxIntervals` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0 w-24">
																	<FormControl>
																		<Input
																			{...field}
																			type="number"
																			min={1}
																			onChange={e => {
																				const value = e.target.value === '' ? null : Number.parseInt(e.target.value, 10);
																				field.onChange(value === null || Number.isNaN(value) ? null : value);
																			}}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.timesLabel', 'times')}
														</span>
													</div>
												</div>
											)
										}
									]}
									renderHeaderActions={({ index }) => (
										<div className="flex items-center justify-between w-full">
											<h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
												{t('form.step', 'Step')} {index + 1}
											</h4>
										</div>
									)}
								/>
								{(form.formState.errors as any)?.rate?.rateSteps?.message && (
									<Alert variant="destructive" className="mt-4">
										<AlertCircle className="w-4 h-4" />
										<AlertDescription>
											{(form.formState.errors as any).rate.rateSteps.message}
										</AlertDescription>
									</Alert>
								)}
							</div>
						)}
					</div>
					</Card>

					{/* Overstay Rate Section */}
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.overstayRate', 'Overstay Rate')}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t('form.overstayRateDescription', 'Optional rate applied when parking duration exceeds the primary rate period.')}
							</p>
						</div>

					<div className="space-y-6">
						<div>
							<Label className="text-sm font-medium">{t('form.rateType', 'Rate Type')}</Label>
							<RadioGroup
								value={overstayRateTypeEnum || 'NONE'}
								onValueChange={handleOverstayRateTypeChange as (value: string) => void}
								className="flex flex-row space-x-6 mt-2"
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="NONE" id="overstay-none" />
									<Label htmlFor="overstay-none" className="text-sm cursor-pointer">
										{t('form.noOverstayRate', 'None')}
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="TIERED" id="overstay-tiered" />
									<Label htmlFor="overstay-tiered" className="text-sm cursor-pointer">
										{getRateTypeTitle(RateTypeEnum.Tiered)}
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="FLAT_RATE" id="overstay-flat" />
									<Label htmlFor="overstay-flat" className="text-sm cursor-pointer">
										{getRateTypeTitle(RateTypeEnum.FlatRate)}
									</Label>
								</div>
							</RadioGroup>
						</div>

						{overstayRateTypeEnum && !isOverstayTiered && (
							<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								<FormField
									control={form.control}
									name="overstayRate.flatRateAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('form.flatRateAmount', 'Flat Rate Amount')}</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="number"
													step="0.01"
													min={0}
													value={field.value ?? ''}
													onChange={e => field.onChange(
														e.target.value === '' ? null : Number.parseFloat(e.target.value) || 0
													)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}

						{overstayRateTypeEnum && isOverstayTiered && (
							<div className="space-y-4">
								<FieldList
									form={form}
									name="overstayRate.rateSteps"
									className="space-y-4"
									itemsGridClassName="flex flex-col gap-4"
									title={t('form.overstayPricingSteps', 'Overstay Pricing Steps')}
									description={t('form.stepEmptyMessage', 'Add a step to define pricing steps.')}
									defaultItem={createEmptyStep()}
									idField="rateStepId"
									forceHardDelete={true}
									addButtonLabel={t('form.addStep', 'Add Step')}
									emptyMessage={t('form.stepEmptyMessage', 'Add a step to define pricing steps.')}
									fields={[
										{
											name: 'intervalMinutes',
											isKeyField: true,
											colSpan: 'col-span-full',
											render: ({ form: f, path, isNewItem }) => (
												<div className="space-y-4">
													{isNewItem && (
														<div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
															<Plus className="w-4 h-4" />
															<span>{t('form.addStep', 'Add Step')}</span>
														</div>
													)}
													<div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.chargeLabel', 'Charge')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.intervalCharge` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0 w-32">
																	<FormControl>
																		<Input
																			{...field}
																			type="number"
																			min={0}
																			step="0.01"
																			onChange={e => {
																				const value = e.target.value === '' ? null : Number.parseFloat(e.target.value);
																				field.onChange(Number.isNaN(value) ? null : value);
																			}}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.perLabel', 'per')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.intervalMinutes` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0">
																	<FormControl>
																		<PeriodInput value={field.value} onChange={field.onChange} />
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.minutesLabel', 'minutes')}
														</span>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.forMaximumLabel', 'for maximum')}
														</span>
														<FormField
															control={f.control}
															name={`${path}.maxIntervals` as any}
															render={({ field }) => (
																<FormItem className="flex-shrink-0 w-24">
																	<FormControl>
																		<Input
																			{...field}
																			type="number"
																			min={1}
																			onChange={e => {
																				const value = e.target.value === '' ? null : Number.parseInt(e.target.value, 10);
																				field.onChange(value === null || Number.isNaN(value) ? null : value);
																			}}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
															{t('form.timesLabel', 'times')}
														</span>
													</div>
												</div>
											)
										}
									]}
									renderHeaderActions={({ index }) => (
										<div className="flex items-center justify-between w-full">
											<h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
												{t('form.step', 'Step')} {index + 1}
											</h4>
										</div>
									)}
								/>
							</div>
						)}
					</div>
					</Card>

					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.limitsAndBillingSection')}
							</h2>
						</div>

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="limit.minAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.minAmount', 'Min Amount')}</FormLabel>
									<FormControl>
										<Input
											type="number"
											step="0.01"
											min={0}
											value={field.value ?? ''}
											onChange={e => field.onChange(
												e.target.value === '' ? null : Number.parseFloat(e.target.value)
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="limit.maxAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.maxAmount', 'Max Amount')}</FormLabel>
									<FormControl>
										<Input
											type="number"
											step="0.01"
											min={0}
											value={field.value ?? ''}
											onChange={e => field.onChange(
												e.target.value === '' ? null : Number.parseFloat(e.target.value)
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="limit.minStayMinutes"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.minStayMinutes', 'Min Stay (minutes)')}</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											value={field.value ?? ''}
											onChange={e => field.onChange(
												e.target.value === '' ? null : Number.parseInt(e.target.value, 10)
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="limit.maxStayMinutes"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.maxStayMinutes', 'Max Stay (minutes)')}</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											value={field.value ?? ''}
											onChange={e => field.onChange(
												e.target.value === '' ? null : Number.parseInt(e.target.value, 10)
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="billingCycleId"
							rules={{ required: 'Billing cycle is required.' }}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.billingCycle')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={billingCycles.map(
												cycle => ({
													value: String(
														cycle.billingCycleId
													),
													label: cycle.name
												})
											)}
											value={field.value || ''}
											onChange={value =>
												field.onChange(value)
											}
											placeholder={t(
												'form.billingCyclePlaceholder'
											)}
											disabled={!billingCycles.length}
											searchable
											className="w-full"
											autoSelectFirstOption={true}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					</Card>
			</form>
		</Form>
		</>
	);
};
